import React, { useState, useEffect, useCallback } from 'react';
import {
  Database,
  Download,
  Trash2,
  RefreshCw,
  Plus,
  RotateCcw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  HardDrive,
  FileArchive,
  PlayCircle,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import { backupService } from '@/services/backupService';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import type {
  BackupJob,
  BackupStatistics,
  BackupStatus,
  BackupSchedule,
  RestoreStatus,
} from '@/types/backup.types';

// Status badge component
const StatusBadge: React.FC<{ status: BackupStatus | RestoreStatus }> = ({ status }) => {
  const config = {
    PENDING: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    RUNNING: { color: 'bg-blue-100 text-blue-800 animate-pulse', icon: PlayCircle },
    COMPLETED: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
    FAILED: { color: 'bg-red-100 text-red-800', icon: XCircle },
    CANCELLED: { color: 'bg-gray-100 text-gray-800', icon: XCircle },
    VALIDATING: { color: 'bg-purple-100 text-purple-800', icon: AlertCircle },
    ROLLED_BACK: { color: 'bg-orange-100 text-orange-800', icon: RotateCcw },
  };

  const { color, icon: Icon } = config[status as keyof typeof config] || config.PENDING;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>
      <Icon size={14} />
      {status}
    </span>
  );
};

// Format bytes
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// Format duration
const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}m ${secs}s`;
};

// Create Backup Modal
const CreateBackupModal: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
}> = ({ onClose, onSuccess }) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    try {
      setLoading(true);
      await backupService.createBackup({ notes });
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to create backup:', error);
      alert('Échec de la création de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600">
          <h3 className="text-lg font-semibold text-white">Créer une Sauvegarde</h3>
          <button
            onClick={onClose}
            className="p-1 text-white/80 hover:text-white hover:bg-white/10 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optionnel)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="Ajoutez des notes sur cette sauvegarde..."
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> La sauvegarde sera exécutée en arrière-plan. Vous pouvez continuer à utiliser le système pendant le processus.
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              onClick={handleCreate}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Database size={18} />
              {loading ? 'Création...' : 'Créer la Sauvegarde'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create Schedule Modal
const CreateScheduleModal: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
}> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    frequency: 'DAILY' as 'DAILY' | 'WEEKLY' | 'MONTHLY',
    hour: 2,
    minute: 0,
    dayOfWeek: 0, // Sunday
    dayOfMonth: 1,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    retentionDays: 30,
    maxBackups: 10,
  });
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      alert('Le nom est requis');
      return;
    }

    try {
      setLoading(true);
      const payload: any = {
        name: formData.name,
        description: formData.description || undefined,
        frequency: formData.frequency,
        hour: formData.hour,
        minute: formData.minute,
        timezone: formData.timezone,
        retentionDays: formData.retentionDays,
        maxBackups: formData.maxBackups || undefined,
      };

      if (formData.frequency === 'WEEKLY') {
        payload.dayOfWeek = formData.dayOfWeek;
      } else if (formData.frequency === 'MONTHLY') {
        payload.dayOfMonth = formData.dayOfMonth;
      }

      await backupService.createSchedule(payload);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to create schedule:', error);
      alert('Échec de la création du planning');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-purple-600 to-pink-600 sticky top-0">
          <h3 className="text-lg font-semibold text-white">Planifier des Sauvegardes Automatiques</h3>
          <button
            onClick={onClose}
            className="p-1 text-white/80 hover:text-white hover:bg-white/10 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom du Planning <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="ex: Sauvegarde quotidienne"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (optionnel)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              rows={2}
              placeholder="Description du planning..."
            />
          </div>

          {/* Frequency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fréquence
            </label>
            <select
              value={formData.frequency}
              onChange={(e) => setFormData({ ...formData, frequency: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="DAILY">Quotidienne</option>
              <option value="WEEKLY">Hebdomadaire</option>
              <option value="MONTHLY">Mensuelle</option>
            </select>
          </div>

          {/* Day of Week (for WEEKLY) */}
          {formData.frequency === 'WEEKLY' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jour de la semaine
              </label>
              <select
                value={formData.dayOfWeek}
                onChange={(e) => setFormData({ ...formData, dayOfWeek: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="0">Dimanche</option>
                <option value="1">Lundi</option>
                <option value="2">Mardi</option>
                <option value="3">Mercredi</option>
                <option value="4">Jeudi</option>
                <option value="5">Vendredi</option>
                <option value="6">Samedi</option>
              </select>
            </div>
          )}

          {/* Day of Month (for MONTHLY) */}
          {formData.frequency === 'MONTHLY' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jour du mois (1-31)
              </label>
              <input
                type="number"
                min="1"
                max="31"
                value={formData.dayOfMonth}
                onChange={(e) => setFormData({ ...formData, dayOfMonth: parseInt(e.target.value) || 1 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          )}

          {/* Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Heure (0-23)
              </label>
              <input
                type="number"
                min="0"
                max="23"
                value={formData.hour}
                onChange={(e) => setFormData({ ...formData, hour: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minute (0-59)
              </label>
              <input
                type="number"
                min="0"
                max="59"
                value={formData.minute}
                onChange={(e) => setFormData({ ...formData, minute: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>

          {/* Retention */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rétention (jours)
              </label>
              <input
                type="number"
                min="1"
                value={formData.retentionDays}
                onChange={(e) => setFormData({ ...formData, retentionDays: parseInt(e.target.value) || 30 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">Supprimer après X jours</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max sauvegardes
              </label>
              <input
                type="number"
                min="1"
                value={formData.maxBackups}
                onChange={(e) => setFormData({ ...formData, maxBackups: parseInt(e.target.value) || 10 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">Nombre maximum à garder</p>
            </div>
          </div>

          {/* Info */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-sm text-purple-800">
              <strong>Note:</strong> Les sauvegardes seront exécutées automatiquement selon ce planning.
              Le fuseau horaire utilisé est: <strong>{formData.timezone}</strong>
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              onClick={handleCreate}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              <Calendar size={18} />
              {loading ? 'Création...' : 'Créer le Planning'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Backup Management Page
export const BackupManagementPage: React.FC = () => {
  const [statistics, setStatistics] = useState<BackupStatistics | null>(null);
  const [backups, setBackups] = useState<BackupJob[]>([]);
  const [schedules, setSchedules] = useState<BackupSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [restoreConfirm, setRestoreConfirm] = useState<string | null>(null);
  const [deleteScheduleConfirm, setDeleteScheduleConfirm] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [statsRes, backupsRes, schedulesRes] = await Promise.all([
        backupService.getStatistics(),
        backupService.listBackups(page, limit),
        backupService.listSchedules(),
      ]);

      setStatistics(statsRes);
      setBackups(backupsRes.data);
      setTotalPages(backupsRes.pagination.totalPages);
      setTotal(backupsRes.pagination.total);
      setSchedules(schedulesRes);
    } catch (error) {
      console.error('Failed to load backup data:', error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 10 seconds for running backups
  useEffect(() => {
    const hasRunning = backups.some((b) => b.status === 'RUNNING' || b.status === 'PENDING');
    if (!hasRunning) return;

    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, [backups, loadData]);

  const handleDownload = async (id: string) => {
    try {
      await backupService.downloadBackup(id);
    } catch (error) {
      console.error('Failed to download backup:', error);
      alert('Échec du téléchargement');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await backupService.deleteBackup(id);
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete backup:', error);
      alert('Échec de la suppression');
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await backupService.restoreBackup({ backupId: id });
      setRestoreConfirm(null);
      alert('Restauration lancée ! Surveillez le statut dans l\'historique.');
      loadData();
    } catch (error) {
      console.error('Failed to restore backup:', error);
      alert('Échec de la restauration');
    }
  };

  const handleDeleteSchedule = async (id: string) => {
    try {
      await backupService.deleteSchedule(id);
      setDeleteScheduleConfirm(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      alert('Échec de la suppression du planning');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Sauvegardes & Restauration</h1>
              <p className="text-sm text-gray-500 mt-1">
                Gestion des sauvegardes de la base de données
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={loadData}
                disabled={loading}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus size={18} />
                Nouvelle Sauvegarde
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Database size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Sauvegardes</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.total}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Réussies</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.completed}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle size={20} className="text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Échouées</p>
                  <p className="text-2xl font-bold text-gray-900">{statistics.failed}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <HardDrive size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Taille Totale</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatBytes(statistics.totalSize)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Backups List */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600">
            <h2 className="text-lg font-semibold text-white">Liste des Sauvegardes</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Taille
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Durée
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <RefreshCw size={24} className="animate-spin mx-auto text-blue-600 mb-2" />
                      <p className="text-gray-500">Chargement...</p>
                    </td>
                  </tr>
                ) : backups.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <Database size={40} className="mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">Aucune sauvegarde trouvée</p>
                    </td>
                  </tr>
                ) : (
                  backups.map((backup) => (
                    <tr key={backup.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(backup.createdAt).toLocaleDateString('fr-FR')}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(backup.createdAt).toLocaleTimeString('fr-FR')}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <FileArchive size={14} />
                          {backup.type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={backup.status} />
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900">
                          {backup.fileSize ? formatBytes(backup.fileSize) : '-'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900">
                          {backup.durationSeconds ? formatDuration(backup.durationSeconds) : '-'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {backup.status === 'COMPLETED' && (
                            <>
                              <button
                                onClick={() => handleDownload(backup.id)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                                title="Télécharger"
                              >
                                <Download size={18} />
                              </button>
                              <button
                                onClick={() => setRestoreConfirm(backup.id)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"
                                title="Restaurer"
                              >
                                <RotateCcw size={18} />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => setDeleteConfirm(backup.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Supprimer"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Affichage de {(page - 1) * limit + 1} à {Math.min(page * limit, total)} sur {total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="px-3 py-1 text-sm text-gray-700">
                  Page {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Schedules Section */}
        <div className="mt-6 bg-white rounded-xl shadow-sm border">
          <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-600 to-pink-600">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Sauvegardes Planifiées</h2>
              <button
                onClick={() => setShowScheduleModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors text-sm"
              >
                <Plus size={16} />
                Nouveau Planning
              </button>
            </div>
          </div>
          <div className="p-6">
            {schedules.length === 0 ? (
              <div className="text-center py-8">
                <Calendar size={48} className="mx-auto text-gray-400 mb-3" />
                <p className="text-gray-500 mb-4">Aucun planning de sauvegarde configuré</p>
                <button
                  onClick={() => setShowScheduleModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Plus size={18} />
                  Créer votre premier planning
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {schedules.map((schedule) => (
                  <div key={schedule.id} className="border rounded-lg p-4 relative">
                    <button
                      onClick={() => setDeleteScheduleConfirm(schedule.id)}
                      className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Supprimer le planning"
                    >
                      <Trash2 size={16} />
                    </button>
                    <div className="flex items-start justify-between mb-2 pr-8">
                      <div>
                        <h3 className="font-semibold text-gray-900">{schedule.name}</h3>
                        {schedule.description && (
                          <p className="text-sm text-gray-500">{schedule.description}</p>
                        )}
                      </div>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          schedule.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {schedule.isActive ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p className="text-gray-600">
                        <strong>Fréquence:</strong> {schedule.frequency}
                      </p>
                      <p className="text-gray-600">
                        <strong>Horaire:</strong> {String(schedule.hour).padStart(2, '0')}:
                        {String(schedule.minute).padStart(2, '0')}
                      </p>
                      <p className="text-gray-600">
                        <strong>Rétention:</strong> {schedule.retentionDays} jours
                        {schedule.maxBackups && ` (max ${schedule.maxBackups})`}
                      </p>
                      {schedule.nextRunAt && (
                        <p className="text-gray-600">
                          <strong>Prochaine:</strong>{' '}
                          {new Date(schedule.nextRunAt).toLocaleString('fr-FR')}
                        </p>
                      )}
                      {schedule.lastRunAt && (
                        <p className="text-gray-600">
                          <strong>Dernière:</strong>{' '}
                          {new Date(schedule.lastRunAt).toLocaleString('fr-FR')}
                          {schedule.lastStatus && (
                            <span className="ml-2">
                              <StatusBadge status={schedule.lastStatus} />
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateBackupModal onClose={() => setShowCreateModal(false)} onSuccess={loadData} />
      )}

      {deleteConfirm && (
        <ConfirmDialog
          title="Supprimer la sauvegarde"
          message="Êtes-vous sûr de vouloir supprimer cette sauvegarde ? Cette action est irréversible."
          confirmLabel="Supprimer"
          onConfirm={() => handleDelete(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
          type="danger"
        />
      )}

      {restoreConfirm && (
        <ConfirmDialog
          title="Restaurer la sauvegarde"
          message="⚠️ ATTENTION: Cette action va remplacer TOUTES les données actuelles par celles de la sauvegarde. Assurez-vous d'avoir créé une sauvegarde récente avant de continuer."
          confirmLabel="Restaurer"
          onConfirm={() => handleRestore(restoreConfirm)}
          onCancel={() => setRestoreConfirm(null)}
          type="danger"
        />
      )}

      {showScheduleModal && (
        <CreateScheduleModal onClose={() => setShowScheduleModal(false)} onSuccess={loadData} />
      )}

      {deleteScheduleConfirm && (
        <ConfirmDialog
          title="Supprimer le planning"
          message="Êtes-vous sûr de vouloir supprimer ce planning de sauvegarde ? Les sauvegardes existantes ne seront pas affectées."
          confirmLabel="Supprimer"
          onConfirm={() => handleDeleteSchedule(deleteScheduleConfirm)}
          onCancel={() => setDeleteScheduleConfirm(null)}
          type="danger"
        />
      )}
    </div>
  );
};

export default BackupManagementPage;
